/**
 * Wspólne prymitywy wizualne list DLC_05 (Kontrola procesu + TODO).
 * Trzymają się języka aplikacji (ostre rogi, mono uppercase, czerwony akcent),
 * ale porządkują to, co wcześniej rozjeżdżało się w tabelach: pasek liczb zamiast
 * zdania z badge'ami, segmentowane przełączniki zamiast luźnych ramek, wiersze
 * ze wskaźnikiem pilności na krawędzi zamiast zalanego tła.
 */
import React, { useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { InfoDot } from "./labels";

export type Tone = "late" | "warn" | "idle" | "ok";

/** Kolor krawędzi wiersza wg pilności — zamiast zalewania całego wiersza tłem. */
export const ROW_ACCENT: Record<Tone, string> = {
  late: "#dc2626",
  warn: "#d97706",
  idle: "#9ca3af",
  ok: "transparent",
};

/** Klasy pierwszej komórki wiersza — pionowa kreska pilności + oddech. */
export function rowAccentStyle(tone: Tone): React.CSSProperties {
  return { boxShadow: `inset 3px 0 0 ${ROW_ACCENT[tone]}` };
}

/** Wspólne klasy <table> list doradcy: bez pionowych kresek z globalnego CSS
 * (rozbijały tabelę na kratkę), stała szerokość kolumn z <colgroup>. */
export const TABLE_CLS =
  "w-full text-sm table-fixed [&_td+td]:border-l-0 [&_th+th]:border-l-0";

// ── sortowanie klikiem w nagłówek ────────────────────────────────────────────
export type SortDir = "asc" | "desc";
export type SortState = { key: string; dir: SortDir } | null;
export type SortValue = string | number | null | undefined;
export type SortAccessors<T> = Record<string, (row: T) => SortValue>;

/** Puste wartości zawsze na końcu — niezależnie od kierunku, żeby „—" nie zajmowało
 * góry listy po odwróceniu sortowania. */
function cmp(a: SortValue, b: SortValue): number {
  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "pl", { numeric: true, sensitivity: "base" });
}

/** Sortowanie tabeli klikiem w nagłówek: rosnąco → malejąco → kolejność wyjściowa
 * (backend zwraca pozycje od najpilniejszej, więc warto móc do niej wrócić). */
export function useTableSort<T>(rows: T[], accessors: SortAccessors<T>) {
  const [sort, setSort] = useState<SortState>(null);
  // Akcesory to świeży obiekt przy każdym renderze — trzymamy je w ref, żeby nie
  // przeliczać sortu w kółko i nie gubić memoizacji.
  const acc = useRef(accessors);
  acc.current = accessors;
  const toggle = (key: string) =>
    setSort((s) =>
      s?.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : null,
    );
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const get = acc.current[sort.key];
    if (!get) return rows;
    const mul = sort.dir === "asc" ? 1 : -1;
    // Stabilnie: przy równych wartościach zostaje kolejność wyjściowa (pilność).
    return rows
      .map((row, idx) => ({ row, idx }))
      .sort((a, b) => {
        const d = cmp(get(a.row), get(b.row));
        return d !== 0 ? mul * d : a.idx - b.idx;
      })
      .map((x) => x.row);
  }, [rows, sort]);
  return { sort, toggle, sorted };
}

export function Th({ children, className, align = "left", info, sortKey, sort, onSort }: {
  children?: React.ReactNode;
  className?: string;
  align?: "left" | "right";
  /** Treść dymka „i" — poza przyciskiem sortowania, żeby najazd nie sortował. */
  info?: string;
  sortKey?: string;
  sort?: SortState;
  onSort?: (key: string) => void;
}) {
  const active = !!sortKey && sort?.key === sortKey;
  const sortable = !!sortKey && !!onSort;
  return (
    <th
      className={clsx(
        // Bez `whitespace-nowrap`: przy table-fixed długi nagłówek wychodziłby poza
        // komórkę i robił poziomy pasek przewijania na całej tabeli.
        "py-2 px-2 font-mono text-[10px] font-bold uppercase tracking-widest leading-tight",
        "text-[var(--color-text-muted)] align-bottom",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
      aria-sort={active ? (sort?.dir === "asc" ? "ascending" : "descending") : undefined}
    >
      <span className={clsx("inline-flex items-baseline gap-1", align === "right" && "justify-end")}>
        {sortable ? (
          <button
            type="button"
            onClick={() => onSort(sortKey)}
            title="Kliknij, aby posortować (rosnąco / malejąco / bez sortowania)"
            className={clsx(
              "group inline-flex items-baseline gap-1 text-left uppercase tracking-widest transition-colors",
              active ? "text-[var(--color-primary)]" : "hover:text-[var(--color-text-primary)]",
            )}
          >
            <span>{children}</span>
            <span
              className={clsx(
                "text-[9px] leading-none",
                active ? "opacity-100" : "opacity-0 group-hover:opacity-40",
              )}
            >
              {active && sort?.dir === "desc" ? "▼" : "▲"}
            </span>
          </button>
        ) : (
          <span>{children}</span>
        )}
        {info && <InfoDot text={info} />}
      </span>
    </th>
  );
}

export function TRow({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <tr
      className="border-b border-[var(--color-border-muted)] align-top hover:bg-[var(--color-bg-elevated)] transition-colors"
      style={rowAccentStyle(tone)}
    >
      {children}
    </tr>
  );
}

export function Td({ children, className, align = "left" }: {
  children?: React.ReactNode; className?: string; align?: "left" | "right";
}) {
  return (
    <td className={clsx("py-2 px-2", align === "right" && "text-right", className)}>
      {children}
    </td>
  );
}

/** Etykieta typu pozycji nad nazwą — zamiast mono-prefiksu doklejonego w linii.
 * Z `onClick` nazwa staje się przyciskiem otwierającym pozycję (kamień / działka /
 * sprawa) w jej własnym widoku. */
export function ItemCell({ kind, label, title, onClick }: {
  kind: string; label: string; title?: string; onClick?: () => void;
}) {
  return (
    <div className="leading-tight min-w-0">
      <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--color-text-muted)]">
        {kind}
      </div>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          title={title ?? "Otwórz szczegóły"}
          className="text-left font-semibold text-[var(--color-text-primary)] break-words hover:text-[var(--color-primary)] hover:underline underline-offset-2"
        >
          {label}
        </button>
      ) : (
        <div className="font-semibold text-[var(--color-text-primary)] break-words" title={title ?? label}>
          {label}
        </div>
      )}
    </div>
  );
}

const COURT_DOT: Record<string, string> = {
  ours: "#ea580c", office: "#2563eb", negotiator: "#7c3aed", blocked: "#9ca3af",
};

/** „Czyj ruch" jako pastylka z kropką — jeden wzór w obu listach. */
export function CourtPill({ court, label }: { court: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-0.5 font-mono text-[10px] leading-tight uppercase tracking-wide text-[var(--color-text-secondary)]">
      <span className="w-1.5 h-1.5 shrink-0" style={{ background: COURT_DOT[court] ?? "#9ca3af" }} />
      {label}
    </span>
  );
}

/** Termin: data w pierwszej linii, stan terminu w drugiej — zamiast jednego
 * długiego czerwonego zdania rozpychającego kolumnę. */
export function DeadlineCell({ date, state, tone, title }: {
  date: string | null; state: string; tone: Tone; title?: string;
}) {
  return (
    <div className="leading-tight" title={title}>
      <div className="font-mono text-xs text-[var(--color-text-primary)]">{date ?? "—"}</div>
      <div
        className={clsx(
          "font-mono text-[10px]",
          tone === "late" ? "text-red-600 font-bold"
            : tone === "warn" ? "text-amber-600 font-bold"
              : "text-[var(--color-text-muted)]",
        )}
      >
        {state}
      </div>
    </div>
  );
}

/** Pasek liczb nad listą — jedna ramka, komórki rozdzielone kreską. */
export function StatStrip({ stats }: {
  stats: { label: React.ReactNode; value: React.ReactNode; tone?: Tone | "none"; title?: string }[];
}) {
  return (
    <div className="flex flex-wrap border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      {stats.map((s, i) => (
        <div
          key={i}
          title={s.title}
          className={clsx(
            "flex-1 min-w-[130px] px-3 py-2",
            i > 0 && "border-l border-[var(--color-border)]",
          )}
        >
          <div className="section-label">{s.label}</div>
          <div
            className={clsx(
              "font-mono text-lg font-bold leading-tight mt-0.5",
              s.tone === "late" ? "text-red-600"
                : s.tone === "warn" ? "text-amber-600"
                  : s.tone === "ok" ? "text-emerald-600"
                    : s.tone === "idle" ? "text-[var(--color-text-secondary)]"
                      : "text-[var(--color-text-primary)]",
            )}
          >
            {s.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Przełącznik jednokrotnego wyboru jako zwarta segmentówka (aktywny = czerwony). */
export function Segmented({ value, onChange, options, title, className }: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
  title?: string;
  className?: string;
}) {
  return (
    <div
      title={title}
      className={clsx("inline-flex border border-[var(--color-border)] bg-[var(--color-bg-surface)]", className)}
    >
      {options.map(([v, l], i) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={clsx(
            "px-3 py-1.5 font-mono text-[11px] uppercase tracking-wide transition-colors",
            i > 0 && "border-l border-[var(--color-border)]",
            value === v
              ? "bg-[var(--color-primary)] text-white"
              : "text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]",
          )}
        >
          {l}
        </button>
      ))}
    </div>
  );
}
